<script setup lang="ts">
import 'swiper/css'

import { usePrice } from '@/composables/price'
import { useTracker } from '@/composables/tracker'
import useAuthStore from '@/stores/auth'
import useCartStore from '@/stores/cart'
import useProductStore from '@/stores/product'
import Icon from 'lilasia-icons'
import { AppButton } from 'lilasia-ui'
import { storeToRefs } from 'pinia'
import type { Swiper } from 'swiper/types'
import { Swiper as AppSwiper, SwiperSlide } from 'swiper/vue'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import FadeTransition from '../Transitions/FadeTransition.vue'
import AppImage from '../common/AppImage.vue'
import ImageSlideIndicator from './ImageSlideIndicator.vue'

const props = defineProps<{
  imageIndex: number
  product: Product
}>()

const state = defineModel<boolean>()

const { trackAddToCart } = useTracker()
const { formatMoney } = usePrice()
const { t } = useI18n()

const authStore = useAuthStore()
const cartStore = useCartStore()
const productStore = useProductStore()

const { addedToCartStatus, activeImageIndex } = storeToRefs(productStore)
const swiper = ref<Swiper | null>(null)

const decoyPrice = computed(() => {
  if (props.product.promotions?.length) {
    return props.product.promotions[0].decoy_price
  }

  return undefined
})

const discount = computed(() => {
  const DECOY_PRICE = decoyPrice.value ?? 0
  const PRICE = props.product.price ?? 0
  const percentage = Math.ceil(((DECOY_PRICE - PRICE) / DECOY_PRICE) * 100)

  return `${percentage}% OFF`
})

const images = computed(() => {
  return props.product.images
})

watch(state, (newState) => {
  const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth

  if (newState) {
    document.body.style.overflow = 'hidden'
    document.body.style.paddingRight = `${scrollBarWidth}px`

    setTimeout(() => {
      document.addEventListener('click', handleClickOutside)
      document.addEventListener('keyup', handleEscapeKey)
    })

    return
  }

  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keyup', handleEscapeKey)

  setTimeout(() => {
    document.body.style.overflow = 'auto'
    document.body.style.paddingRight = '0'
  }, 300)
})

watch(
  () => swiper.value?.activeIndex,
  (newActiveIndex) => {
    if (typeof newActiveIndex === 'number') {
      activeImageIndex.value = newActiveIndex
    }
  }
)

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown)
})

onBeforeUnmount(() => {
  document.body.style.overflow = 'auto'
  document.body.style.paddingRight = '0'
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keyup', handleEscapeKey)
  window.removeEventListener('keydown', handleKeyDown)
})

const setSwiper = (instance: Swiper) => {
  swiper.value = instance
  swiper.value?.slideTo(props.imageIndex)
}

const handleAddToCart = () => {
  if (!authStore.isAuthenticated) {
    trackAddToCart(props.product, cartStore.quantity, true)
    authStore.showAuthModal()
    return
  }

  if (cartStore.productExistedInCart) {
    const quantity = cartStore.productExistedInCart.quantity + cartStore.quantity
    cartStore.updateItem({
      product: props.product,
      quantity,
      notes: cartStore.productExistedInCart.notes
    })

    trackAddToCart(props.product, quantity)
  } else {
    cartStore.addItem({
      product: props.product,
      quantity: cartStore.quantity,
      notes: cartStore.notes
    })

    cartStore.notes = null
    trackAddToCart(props.product, cartStore.quantity)
  }

  // Showing Added To Cart Status for a 3 seconds
  addedToCartStatus.value = true
  setTimeout(() => {
    addedToCartStatus.value = false
  }, 3000)
}
const handleClickOutside = (event: MouseEvent) => {
  const content = document.querySelector('.product-gallery-grid')

  if (!content?.contains(event.target as Node)) {
    state.value = false
  }
}

const handleEscapeKey = (event: KeyboardEvent) => {
  event.preventDefault()

  if (event.key === 'Escape') {
    state.value = false
  }
}

const handleKeyDown = (event: any) => {
  if (event.key === 'ArrowLeft' && !swiper.value?.isBeginning) {
    swiper.value?.slidePrev()
  } else if (event.key === 'ArrowRight' && !swiper.value?.isEnd) {
    swiper.value?.slideNext()
  }
}
</script>

<template>
  <FadeTransition>
    <div
      v-if="state"
      class="relative z-50 text-white"
    >
      <div class="fixed right-16 top-16 z-10">
        <AppButton
          icon="close"
          round
          aria-label="Close product gallery"
          @click="state = false"
        />
      </div>

      <div class="fixed inset-0 bg-black/85"></div>

      <div class="container fixed inset-0 mx-auto flex w-full items-center">
        <div class="product-gallery-grid">
          <div>
            <AppSwiper
              :space-between="8"
              class="relative"
              @swiper="setSwiper"
            >
              <SwiperSlide
                v-for="(image, index) in product.images"
                :key="index"
              >
                <AppImage
                  v-if="image.image_url"
                  :src="image.image_url"
                  :alt="product.name"
                  :width="800"
                  :height="800"
                  class="h-full w-full rounded-8 bg-white object-cover"
                />
              </SwiperSlide>

              <!-- Navigation -->
              <FadeTransition group>
                <div
                  v-if="!swiper?.isBeginning"
                  class="absolute left-36 top-1/2 z-10 -translate-y-1/2"
                >
                  <button
                    class="flex size-44 items-center justify-center rounded-9999 border-2 border-black-10 bg-white text-black-60"
                    aria-label="Slide previous"
                    @click="swiper?.slidePrev()"
                  >
                    <Icon
                      name="keyboard_arrow_left"
                      size="44"
                    />
                  </button>
                </div>

                <div
                  v-if="!swiper?.isEnd"
                  class="absolute right-36 top-1/2 z-10 -translate-y-1/2"
                >
                  <button
                    class="flex size-44 items-center justify-center rounded-9999 border-2 border-black-10 bg-white text-black-60"
                    aria-label="Slide Next"
                    @click="swiper?.slideNext()"
                  >
                    <Icon
                      name="keyboard_arrow_right"
                      size="44"
                      class="ml-[3px]"
                    />
                  </button>
                </div>
              </FadeTransition>

              <!-- Stats -->
              <div class="absolute bottom-16 left-16 z-10">
                <ImageSlideIndicator
                  v-if="images && images.length > 1 && activeImageIndex.toString()"
                  :current="activeImageIndex + 1"
                  :total="images.length"
                />
              </div>
            </AppSwiper>
          </div>

          <div class="flex flex-col gap-16">
            <div
              v-if="product.images?.length"
              class="grid grid-cols-5 gap-8"
            >
              <AppImage
                v-for="(image, index) in product.images"
                :key="index"
                :src="image.thumbnail_url"
                :alt="product.name"
                :width="74"
                :height="74"
                :class="{
                  'h-full w-full rounded-8 bg-white object-cover transition-opacity': true,
                  'opacity-100': swiper?.activeIndex === index,
                  'opacity-50': swiper?.activeIndex !== index
                }"
                role="button"
                @click="swiper?.slideTo(index)"
              />
            </div>

            <div class="text-20 font-500">{{ product.name }}</div>
            <div class="flex items-end justify-between gap-16">
              <div>
                <span class="text-24 font-600">
                  {{ formatMoney(product.price) }}
                </span>
                <div
                  v-if="decoyPrice"
                  class="flex items-center gap-8 text-14 leading-20"
                >
                  <s class="text-black-20">{{ formatMoney(decoyPrice) }}</s>
                  <span
                    class="rounded-4 bg-red-20 px-4 py-[2px] text-12 font-500 leading-14 text-red-100"
                  >
                    {{ discount }}
                  </span>
                </div>
              </div>
              <AppButton
                color="yellow"
                :icon="addedToCartStatus ? 'check' : 'add'"
                :text="addedToCartStatus ? t('added_to_cart') : t('add_to_cart')"
                @click="handleAddToCart"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </FadeTransition>
</template>
